/**
 * Environment configuration utilities
 * Provides environment-aware URL generation and settings
 */

export type Environment = "development" | "staging" | "production";

/**
 * Get current environment from Vite environment variables
 */
export function getCurrentEnvironment(): Environment {
  const env = import.meta.env.VITE_ENVIRONMENT || "development";
  return env as Environment;
}

/**
 * Get the current frontend URL based on environment
 */
export function getFrontendUrl(): string {
  // Check for local development override
  if (import.meta.env.VITE_LOCAL_APP_URL) {
    return import.meta.env.VITE_LOCAL_APP_URL;
  }

  const environment = getCurrentEnvironment();

  switch (environment) {
    case "production":
      return "https://thepearl.ky";
    case "staging":
      return "https://staging.thepearl.ky";
    case "development":
    default:
      // For development, prefer deployed dev domain but fallback to localhost
      return import.meta.env.DEV
        ? "http://localhost:8080"
        : "https://dev.thepearl.ky";
  }
}

/**
 * Get Supabase configuration from environment variables
 */
export function getSupabaseConfig() {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      "Missing Supabase configuration. VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set."
    );
  }

  return { url, anonKey };
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  return getCurrentEnvironment() === "development";
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return getCurrentEnvironment() === "production";
}

/**
 * Get environment-specific configuration
 */
export function getEnvironmentConfig() {
  const environment = getCurrentEnvironment();

  return {
    environment,
    frontendUrl: getFrontendUrl(),
    supabase: getSupabaseConfig(),
    debug: isDevelopment(),
    apiTimeout: environment === "production" ? 60000 : 45000,
  };
}
